"use client"

import { useState } from "react"
import { Check, ChevronDown, Loader2 } from "lucide-react"

import { CustomFilterIcon } from "@/components/custom-filter-icon"
import { AppliedFilterPills, type AppliedFilterPill } from "@/components/grid/applied-filter-pills"
import { ModernDatePicker } from "@/components/modern-date-picker"
import { Button } from "@/components/ui/button"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useTranslation } from "@/hooks/use-translation"
import { EMPTY_ATTRIBUTE_FILTERS, hasActiveAttributeFilters, type InboxAttributeFilters, type collectFilterOptions } from "@/lib/inbox-filters"

type FilterOptions = ReturnType<typeof collectFilterOptions>

// Never mutate the incoming arrays — `filters` may be the shared EMPTY_ATTRIBUTE_FILTERS constant.
const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v])

/** Number of active facets — the badge on the filter button. */
function countActiveFacets(f: InboxAttributeFilters): number {
  return [f.from !== null || f.to !== null, f.stages.length + f.requestTypes.length > 0, f.senders.length > 0, f.clients.length > 0].filter(Boolean).length
}

interface OptionGroup {
  heading?: string
  /** Facet prefix for the cmdk value, so the same label in two groups (e.g. "Onboarding") stays distinct. */
  prefix: string
  values: string[]
  selected: string[]
  onToggle: (v: string) => void
}

// A labelled multi-select inside the filter popover: a trigger showing the selection, opening a searchable
// checklist. Portals into the filter popover's own container so it works inside the dialog's focus trap.
function MultiSelectField({ label, groups, portalContainer }: { label: string; groups: OptionGroup[]; portalContainer: HTMLElement | null }) {
  const { t } = useTranslation()
  const selected = groups.flatMap((g) => g.selected)
  const summary =
    selected.length === 0 ? t("inboxFilterAny") : selected.length <= 2 ? selected.join(", ") : t("inboxFilterSelectedCount", { count: selected.length })
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-8 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <span className={`truncate ${selected.length === 0 ? "text-muted-foreground" : ""}`}>{summary}</span>
            <ChevronDown size={14} className="ml-2 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent portalContainer={portalContainer} align="start" sideOffset={4} className="w-64 p-0">
          <Command>
            <CommandInput placeholder={t("search")} />
            <CommandList className="max-h-60">
              <CommandEmpty>{t("noRecordsFound")}</CommandEmpty>
              {groups.filter((g) => g.values.length > 0).map((g) => (
                <CommandGroup key={g.prefix} heading={g.heading}>
                  {g.values.map((v) => (
                    <CommandItem key={`${g.prefix}:${v}`} value={`${g.prefix}:${v}`} keywords={[v]} onSelect={() => g.onToggle(v)}>
                      <Check size={14} className={g.selected.includes(v) ? "opacity-100" : "opacity-0"} />
                      <span className="truncate">{v}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

interface MessagesInboxFilterButtonProps {
  filters: InboxAttributeFilters
  onChange: (next: InboxAttributeFilters) => void
  options: FilterOptions
  showClientFilter: boolean
  /** Rows are still being enriched, so the Stage / Client options may still grow. */
  optionsLoading: boolean
  /** Controlled so the modal can fetch every row's metadata while the popover is open (complete options). */
  open: boolean
  onOpenChange: (open: boolean) => void
  portalContainer: HTMLElement | null
}

// Filter icon (with an active-facet count) opening one popover with every attribute filter — the same
// pattern as the ATS sync-settings FiltersPopover. Presentational: semantics live in lib/inbox-filters.ts and
// the modal owns the state.
export function MessagesInboxFilterButton({ filters, onChange, options, showClientFilter, optionsLoading, open, onOpenChange, portalContainer }: MessagesInboxFilterButtonProps) {
  const { t } = useTranslation()
  // Nested pickers (dates, multi-selects) portal into the popover itself, not the body: a picker portaled
  // outside the dialog loses its clicks to the dialog's focus trap (the calendar dismisses before the day lands).
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const activeCount = countActiveFacets(filters)

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="elliptical-outline" className="relative h-8 min-h-0 shrink-0 gap-1.5 px-3 text-xs" aria-label={t("inboxFilters")}>
          <CustomFilterIcon size={14} />
          {t("inboxFilters")}
          {activeCount > 0 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] font-semibold leading-none text-white">
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-72 flex-col gap-0 p-0" portalContainer={portalContainer}>
        <div ref={setContainer} className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-gray-700">{t("inboxFilterDate")}</span>
            <div className="flex flex-col gap-2">
              <ModernDatePicker
                value={filters.from}
                onChange={(v) => onChange({ ...filters, from: v })}
                placeholder={t("inboxFilterFrom")}
                maxDate={filters.to}
                portalContainer={container}
              />
              <ModernDatePicker
                value={filters.to}
                onChange={(v) => onChange({ ...filters, to: v })}
                placeholder={t("inboxFilterTo")}
                minDate={filters.from}
                portalContainer={container}
              />
            </div>
          </div>

          <MultiSelectField
            label={t("stage")}
            portalContainer={container}
            groups={[
              {
                heading: t("inboxFilterWorkOrderStages"),
                prefix: "stage",
                values: options.stages,
                selected: filters.stages,
                onToggle: (v) => onChange({ ...filters, stages: toggle(filters.stages, v) }),
              },
              {
                heading: t("inboxFilterRequests"),
                prefix: "request",
                values: options.requestTypes,
                selected: filters.requestTypes,
                onToggle: (v) => onChange({ ...filters, requestTypes: toggle(filters.requestTypes, v) }),
              },
            ]}
          />

          <MultiSelectField
            label={t("inboxFilterSender")}
            portalContainer={container}
            groups={[{ prefix: "sender", values: options.senders, selected: filters.senders, onToggle: (v) => onChange({ ...filters, senders: toggle(filters.senders, v) }) }]}
          />

          {showClientFilter && (
            <MultiSelectField
              label={t("client")}
              portalContainer={container}
              groups={[{ prefix: "client", values: options.clients, selected: filters.clients, onToggle: (v) => onChange({ ...filters, clients: toggle(filters.clients, v) }) }]}
            />
          )}

          <div className="flex items-center justify-between">
            {/* Until the background enrich finishes, the stage / client options (and the filtered list) may still
                grow. */}
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              {optionsLoading && (<><Loader2 size={12} className="animate-spin" /> {t("loading")}</>)}
            </span>
            <Button
              variant="ghost"
              className="h-7 min-h-0 px-2 text-xs"
              disabled={!hasActiveAttributeFilters(filters)}
              onClick={() => onChange(EMPTY_ATTRIBUTE_FILTERS)}
            >
              {t("clearAll")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface MessagesInboxFilterPillsProps {
  filters: InboxAttributeFilters
  onChange: (next: InboxAttributeFilters) => void
  /** Clears these filters AND the search box. */
  onClearAll: () => void
}

// One removable pill per active facet (the grid's applied-filter pills), plus "Clear filters" (which also clears
// the search). Renders nothing while no attribute filter is set — the search box has its own clear button — so
// the inbox only grows a row when a filter is applied.
export function MessagesInboxFilterPills({ filters, onChange, onClearAll }: MessagesInboxFilterPillsProps) {
  const { t } = useTranslation()
  if (!hasActiveAttributeFilters(filters)) return null

  const valuesPill = (key: string, label: string, values: string[], onRemove: () => void): AppliedFilterPill => ({
    key,
    scope: "global",
    label,
    preview: values.slice(0, 2).join(", "),
    overflow: Math.max(0, values.length - 2),
    onRemove,
  })
  const pills: AppliedFilterPill[] = []
  if (filters.from !== null || filters.to !== null) {
    pills.push({
      key: "date",
      scope: "global",
      label: t("inboxFilterDate"),
      preview: [filters.from ?? "…", filters.to ?? "…"].join(" → "),
      overflow: 0,
      onRemove: () => onChange({ ...filters, from: null, to: null }),
    })
  }
  const stageValues = [...filters.stages, ...filters.requestTypes]
  if (stageValues.length > 0) pills.push(valuesPill("stage", t("stage"), stageValues, () => onChange({ ...filters, stages: [], requestTypes: [] })))
  if (filters.senders.length > 0) pills.push(valuesPill("sender", t("inboxFilterSender"), filters.senders, () => onChange({ ...filters, senders: [] })))
  if (filters.clients.length > 0) pills.push(valuesPill("client", t("client"), filters.clients, () => onChange({ ...filters, clients: [] })))

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b px-5 py-2">
      <AppliedFilterPills pills={pills} maxVisible={pills.length} dense={false} />
      <Button variant="ghost" className="h-7 min-h-0 px-2 text-xs text-muted-foreground" onClick={onClearAll}>
        {t("clearFilters")}
      </Button>
    </div>
  )
}
